'use client'

import dynamic from 'next/dynamic'
import { C, Callout, Exercise, Figure, H, Lesson, M, MB, P, Quiz, Step, UL } from '../_components/lesson'
import * as EX from './exercises/nn-extra'

const DeepActivations = dynamic(() => import('../_components/widgets/NNExtraWidgets').then(m => m.DeepActivations), { ssr: false })

export default function DeepTraining() {
  return (
    <Lesson>
      <Step>
        <H>Autograd for whole matrices</H>
        <P>
          Your <C>Value</C> engine tracks one number per node. For real networks, the course’s <C>Tensor</C> class is the same
          idea with a whole matrix in every node. Each operation (<C>matmul</C>, <C>add</C>, <C>tanh</C>…) records how to pass
          gradients back using the formulas from the layers lesson, and <C>.backward()</C> runs them in reverse order. It’s
          thousands of times faster.
        </P>
        <Exercise id="tensor-autograd" title="Tensor, Linear, and a gradient check" {...EX.tensorAutograd} />
        <Callout kind="note">
          <C>Linear</C> bundles a weight matrix and a bias. <C>SGD</C> and <C>Adam</C> are optimisers: objects that apply the
          update step to a list of parameters. You’ll meet Adam properly in the optimizers lesson.
        </Callout>
      </Step>

      <Step gate="q-depth">
        <H>Why depth is hard</H>
        <P>
          Deeper networks can learn more, but they’re harder to train. Each layer multiplies the signal by its weights. Do that
          8 times with weights that are a bit too small and the signal fades to nothing; a bit too big and it explodes, or every
          tanh gets pinned at ±1. The same happens to gradients on the way back. These are the{' '}
          <strong>vanishing</strong> and <strong>exploding</strong> gradient problems.
        </P>
        <Figure n="7.7" wide caption="A batch flowing through 8 layers. Top: the spread of the activations at each layer. Bottom: how big the gradient is when it gets back to each layer. Try each initialisation, then switch on normalisation.">
          <DeepActivations />
        </Figure>
        <Quiz
          id="q-depth"
          question="With ‘too small’ initialisation, why does the network barely learn?"
          options={[
            { label: 'The signal shrinks at every layer, so the deep layers see almost nothing — and the gradients reaching the first layers are tiny', correct: true },
            { label: 'Small weights make the learning rate too big', why: 'The learning rate is separate. The problem is what happens to the signal between layers.' },
            { label: 'Small weights mean fewer parameters', why: 'Same number of parameters — just tiny values.' },
          ]}
        />
      </Step>

      <Step gate="init-experiment">
        <H>Start at the right scale</H>
        <P>
          The fix is to pick each layer’s starting weights so the signal keeps roughly the same spread from layer to layer. A
          layer with <M>n</M> inputs adds up <M>n</M> random terms, so the weights should shrink like <M>{'1/\\sqrt{n}'}</M>:
        </P>
        <MB>{'\\text{Xavier (tanh): } \\sigma = \\frac{1}{\\sqrt{n}} \\qquad \\text{He (ReLU): } \\sigma = \\sqrt{\\frac{2}{n}}'}</MB>
        <Exercise
          id="init-experiment" timeoutMs={60000}
          title="The same network, three starting scales"
          {...EX.initExperiment}
          hints={[
            <>
              <C>{'layer.W = Tensor.randn(sizes[i], sizes[i + 1], std)'}</C>
            </>,
            <>
              <C>{'layers.forEach((l, i) => { h = l.forward(h); if (i < layers.length - 1) h = h.tanh() })'}</C>
            </>,
          ]}
        />
      </Step>

      <Step gate={['residual-norm', 'q-resid']}>
        <H>Two inventions that made depth easy</H>
        <UL>
          <li>
            <strong>Normalisation.</strong> Rescale each example’s activations to mean 0 and spread 1 inside the network
            (<C>layerNorm</C>), so no layer can push the signal off-scale. Then let the network learn its own scale and shift
            (<M>{'\\gamma, \\beta'}</M>).
          </li>
          <li>
            <strong>Residual connections.</strong> Instead of replacing the signal at each layer, <em>add</em> to it:{' '}
            <M>{'h \\leftarrow h + f(h)'}</M>. The gradient then has a direct highway back through all the{' '}
            <M>{'+'}</M>s, so it can’t vanish however deep the network is.
          </li>
        </UL>
        <Exercise
          id="residual-norm" timeoutMs={60000}
          title="20 layers, plain vs. residual"
          {...EX.residualNorm}
          hints={[
            <>
              <C>{'h = h.add(block.forward(h.layerNorm(gammas[i], betas[i])).tanh())'}</C>
            </>,
          ]}
        />
        <Quiz
          id="q-resid"
          question="In h ← h + f(h), what does the gradient of the output with respect to h always include?"
          options={[
            { label: 'A plain copy of the incoming gradient (from the “+ h” part), whatever f does', correct: true },
            { label: 'Nothing — f decides everything', why: 'The derivative of h + f(h) is 1 + f′(h). The 1 passes the gradient straight through.' },
            { label: 'Only the gradient of f', why: 'The added h contributes its own derivative of 1.' },
          ]}
          explain="Every transformer, including the one you’ll build in Module 9, is a stack of residual blocks with layer norm, for exactly this reason."
        />
      </Step>
    </Lesson>
  )
}
